import { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'components/UtilElements';
import { Dispatch } from 'redux';
import { UPDATE_SELECTED_CHILD_COURSE_ID, UPDATE_SELECTED_PARENT_COURSE_ID } from 'redux/actions';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import { ReactComponent as LogoutIcon } from 'assets/logout_active.svg';

interface ParentCourseRow {
  isParent: true;
}

interface ChildCourseRow {
  isParent: false;
  childId: string;
}

type CourseRowProps = (ParentCourseRow | ChildCourseRow) & {
  courseId: string;
  code: string;
  title: string;
  isImported: NumericBoolean;
  updateSelectedParentCourseId: (parentCourseId: string) => void;
  updateSelectedChildCourseId: (childCourseId: string) => void;
};

const CourseRow = ({
  courseId,
  code,
  title,
  isImported,
  updateSelectedParentCourseId,
  updateSelectedChildCourseId,
  ...props
}: CourseRowProps) => {
  return (
    <tr>
      <td className='table__cell table__cell--left'>{code}</td>
      <td className='table__cell table__cell--left'>{title}</td>
      <td className='table__cell table__cell--left'>
        {isImported ? (
          <Link
            to={ROUTES.COURSES}
            component={({ navigate, href }) => {
              return (
                <Button
                  variant='outlined'
                  color='success'
                  size='small'
                  className='import-course__action'
                  onClick={() => {
                    updateSelectedParentCourseId(courseId);
                    if (!props.isParent) updateSelectedChildCourseId(props.childId);
                    navigate(href);
                  }}
                >
                  ACCESS
                </Button>
              );
            }}
          />
        ) : (
          <Link
            to={ROUTES.COURSES_CREATE}
            component={({ navigate, href }) => {
              return (
                <Button
                  variant='outlined'
                  color='info'
                  size='small'
                  className='import-course__action'
                  onClick={() => {
                    updateSelectedParentCourseId(courseId);
                    if (!props.isParent) updateSelectedChildCourseId(props.childId);
                    navigate(href);
                  }}
                >
                  IMPORT
                </Button>
              );
            }}
          />
        )}
      </td>
    </tr>
  );
};

const mapStateToCourseRowProps = () => ({});

const mapDispatchToCourseRowProps = (dispatch: Dispatch) => ({
  updateSelectedParentCourseId: (parentCourseId: string) => {
    dispatch({
      type: UPDATE_SELECTED_PARENT_COURSE_ID,
      payload: parentCourseId,
    });
  },
  updateSelectedChildCourseId: (childCourseId: string) => {
    dispatch({
      type: UPDATE_SELECTED_CHILD_COURSE_ID,
      payload: childCourseId,
    });
  },
});

const ConnectedCourseRow = connect(mapStateToCourseRowProps, mapDispatchToCourseRowProps)(CourseRow);

interface ParentCourseProps {
  courseDetails: SessionData['courseDetails'];
}

const ParentCourse = ({ courseDetails }: ParentCourseProps) => {
  const { courseId, code, title, isImported } = courseDetails;

  return (
    <div className='section'>
      <div className='section__header'>
        <h3 className='section__title'>Import the course as just one course</h3>
        <Link className='logout' to={ROUTES.LOG_OUT}>
          <LogoutIcon className='nav__icon' /> Log Out
        </Link>
      </div>
      <div className='section__body'>
        <table className='table table--bordered'>
          <thead>
            <tr>
              <th className='table__cell table__cell--left table__cell--course-code'>Course Code</th>
              <th className='table__cell table__cell--left table__cell--course-title'>Title</th>
              <th className='table__cell table__cell--course-actions' />
            </tr>
          </thead>
          <tbody>
            <ConnectedCourseRow
              isParent
              courseId={courseId}
              code={code}
              title={title}
              isImported={isImported}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface ChildCoursesProps {
  courseDetails: SessionData['courseDetails'];
}

const ChildCourses = ({ courseDetails }: ChildCoursesProps) => {
  const { courseId, hasChildren, childCourses } = courseDetails;

  if (hasChildren === 0) return null;

  return (
    <div className='section'>
      <div className='section__header'>
        <h3 className='section__title'>Import each child course as a separate course</h3>
      </div>
      <div className='section__body'>
        <table className='table table--bordered'>
          <thead>
            <tr>
              <th className='table__cell table__cell--left table__cell--course-code'>Course Code</th>
              <th className='table__cell table__cell--left table__cell--course-title'>Title</th>
              <th className='table__cell table__cell--course-actions' />
            </tr>
          </thead>
          <tbody>
            {childCourses.map(({ childId, code, title, isImported }) => (
              <ConnectedCourseRow
                key={childId}
                isParent={false}
                courseId={courseId}
                childId={childId}
                code={code}
                title={title}
                isImported={isImported}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export interface SelectCourseProps {
  courseDetails: SessionData['courseDetails'] | undefined;
  updateSelectedParentCourseId: (parentCourseId: string | null) => void;
  updateSelectedChildCourseId: (childCourseId: string | null) => void;
}

const SelectCourse = ({
  courseDetails,
  updateSelectedParentCourseId,
  updateSelectedChildCourseId,
}: SelectCourseProps) => {
  useEffect(() => {
    updateSelectedParentCourseId(null);
    updateSelectedChildCourseId(null);
  }, [updateSelectedParentCourseId, updateSelectedChildCourseId]);

  if (!courseDetails) return null;

  return (
    <div className='page select-course'>
      <div className='page__body'>
        <ParentCourse courseDetails={courseDetails} />
        <ChildCourses courseDetails={courseDetails} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  courseDetails: state.getAccess?.courseDetails,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateSelectedParentCourseId: (parentCourseId: string | null) => {
    dispatch({
      type: UPDATE_SELECTED_PARENT_COURSE_ID,
      payload: parentCourseId,
    });
  },
  updateSelectedChildCourseId: (childCourseId: string | null) => {
    dispatch({
      type: UPDATE_SELECTED_CHILD_COURSE_ID,
      payload: childCourseId,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectCourse);
